import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { ArrowRight, Star, Shield, Award, Clock, CheckCircle, Sparkles, Car, Droplets, Layers } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service icons mapping
const getServiceIcon = (id) => {
  const icons = {
    "exterior-interior": Car,
    "ceramic-coating": Layers,
    "polishing": Sparkles,
    "leather-care": Droplets,
    "ozone": Droplets,
    "premium-wash": Droplets,
  };
  return icons[id] || Sparkles;
};

// Testimonial component
const TestimonialCard = ({ testimonial, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.15 }}
    className="bg-[#0A0A0A] border-l-2 border-[#D4AF37] p-6"
    data-testid={`testimonial-${index}`}
  >
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={16} className={i <= testimonial.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#2A2A2A]"} />
      ))}
    </div>
    <p className="font-accent text-[#E5E5E5] text-lg mb-6 leading-relaxed">"{testimonial.text}"</p>
    <div>
      <p className="font-heading text-[#E5E5E5]">{testimonial.name}</p>
      <p className="text-[#D4AF37] text-sm">{testimonial.car}</p>
    </div>
  </motion.div>
);

// Service Card component
const ServiceCard = ({ service, index }) => {
  const IconComponent = getServiceIcon(service.id);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group relative bg-[#121212] border border-[#2A2A2A] hover:border-[#D4AF37] transition-all duration-300 overflow-hidden"
      data-testid={`service-card-${service.id}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img src={service.image} alt={service.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/50 to-transparent" />
        <div className="absolute top-4 left-4 w-12 h-12 bg-[#D4AF37] rounded-sm flex items-center justify-center">
          <IconComponent size={24} className="text-black" />
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl text-[#E5E5E5] mb-2">{service.name}</h3>
        <p className="text-[#A3A3A3] text-sm mb-4 line-clamp-2">{service.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-[#D4AF37] font-semibold">{service.price_from.toLocaleString()} Ft-tól</span>
          <span className="text-[#666] text-sm">{service.duration}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Trust Badge component
const TrustBadge = ({ badge, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="text-center p-6 bg-[#0A0A0A] border border-[#2A2A2A] hover:border-[#D4AF37] transition-colors"
    data-testid={`trust-badge-${index}`}
  >
    <badge.icon size={36} className="text-[#D4AF37] mx-auto mb-4" />
    <h4 className="font-heading text-lg text-[#E5E5E5] mb-1">{badge.title}</h4>
    <p className="text-[#A3A3A3] text-sm">{badge.desc}</p>
  </motion.div>
);

const HomePage = () => {
  const staticServices = [
    {
      id: "exterior-interior",
      name: "Külső–Belső Detailing",
      description: "Teljes körű autótisztítás kívül és belül",
      price_from: 45000,
      duration: "3-4 óra",
      image: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "ceramic-coating",
      name: "Kerámia Bevonat",
      description: "Hosszú távú védelem a lakk számára",
      price_from: 150000,
      duration: "6-8 óra",
      image: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "polishing",
      name: "Gépi Polírozás",
      description: "Fényvisszaállítás és karcoláseltávolítás",
      price_from: 80000,
      duration: "4-6 óra",
      image: "https://images.unsplash.com/photo-1616422285623-13ff0162193c?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "leather-care",
      name: "Bőrápolás",
      description: "Professzionális bőrtisztítás és kondicionálás",
      price_from: 35000,
      duration: "2-3 óra",
      image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "ozone",
      name: "Ózonos Fertőtlenítés",
      description: "Teljes körű belső fertőtlenítés és szagmentesítés",
      price_from: 25000,
      duration: "1-2 óra",
      image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "premium-wash",
      name: "Prémium Kézi Mosás",
      description: "Professzionális kézi autómosás",
      price_from: 15000,
      duration: "1 óra",
      image: "https://images.unsplash.com/photo-1601362840469-51e4d8d58785?q=80&w=800&auto=format&fit=crop"
    }
  ];

  const [services, setServices] = useState(staticServices);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data.services.slice(0, 6));
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices(staticServices);
      }
    };
    fetchServices();
  }, []);

  const testimonials = [
    { name: "Demo Ügyfél 1", car: "BMW M5", rating: 5, text: "Lenyűgöző munka! Az autóm olyan, mintha most gurult volna ki a szalonból. A kerámia bevonat tökéletes lett. (Demo vélemény)" },
    { name: "Demo Ügyfél 2", car: "Mercedes E-Class", rating: 5, text: "Profi csapat, prémium minőség. A belső tisztítás után olyan az illat, mint egy új autóban. (Demo vélemény)" },
    { name: "Demo Ügyfél 3", car: "Porsche 911", rating: 5, text: "Évek óta kerestem egy megbízható autókozmetikát. Végre megtaláltam. A polírozás eredménye páratlan. (Demo vélemény)" },
  ];

  const trustBadges = [
    { icon: Shield, title: "Garancia", desc: "100% elégedettségi garancia" },
    { icon: Award, title: "Prémium Anyagok", desc: "Csak a legjobb termékek" },
    { icon: Clock, title: "Pontos Határidő", desc: "Időben elkészülünk" },
    { icon: CheckCircle, title: "10+ Év Tapasztalat", desc: "Szakértői csapat" },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center hero-bg" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/80 to-[#050505]" />
        <div className="container mx-auto px-6 relative z-10 py-32">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm uppercase tracking-widest mb-6">
              Prémium Autókozmetika
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#E5E5E5] leading-tight mb-6">
              PRÉMIUM AUTÓKOZMETIKA
              <span className="block text-[#D4AF37]">KOMPROMISSZUMOK NÉLKÜL</span>
            </h1>
            <p className="text-lg text-[#A3A3A3] mb-8 max-w-xl leading-relaxed">
              Bízza ránk autója szépségét és védelmét. Professzionális detailing szolgáltatások, prémium anyagok és szakértői csapat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/idopontfoglalas">
                <Button data-testid="hero-booking-btn" className="btn-gold bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-8 py-6 rounded-sm text-base">
                  Időpont Foglalás <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/szolgaltatasok">
                <Button data-testid="hero-services-btn" variant="outline" className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-6 uppercase tracking-widest rounded-sm text-base">
                  Szolgáltatások
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="services-preview">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">Szolgáltatásaink</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#E5E5E5] mb-4">PRÉMIUM DETAILING SZOLGÁLTATÁSOK</h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto">Válasszon professzionális szolgáltatásaink közül</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/szolgaltatasok">
              <Button data-testid="all-services-btn" variant="outline" className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-4 uppercase tracking-widest rounded-sm">
                Összes Szolgáltatás <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Before/After Slider */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="before-after-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">Eredményeink</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#E5E5E5] mb-4">ELŐTTE – UTÁNA</h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto">Húzza a csúszkát és győződjön meg munkánk minőségéről</p>
          </div>
          <div className="max-w-4xl mx-auto border border-[#2A2A2A] rounded-lg overflow-hidden">
            <ReactCompareSlider
              itemOne={<ReactCompareSliderImage src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?q=80&w=1200&auto=format&fit=crop&sat=-100" alt="Előtte" />}
              itemTwo={<ReactCompareSliderImage src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?q=80&w=1200&auto=format&fit=crop" alt="Utána" />}
              style={{ height: "500px" }}
              data-testid="compare-slider"
            />
            <div className="flex justify-between p-4 bg-[#121212]">
              <span className="text-[#A3A3A3] uppercase tracking-wider text-sm">Előtte</span>
              <span className="text-[#D4AF37] uppercase tracking-wider text-sm">Utána</span>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/galeria">
              <Button data-testid="gallery-btn" className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-8 py-4 rounded-sm">
                Galéria Megtekintése <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 bg-[#050505] border-y border-[#2A2A2A]" data-testid="trust-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustBadges.map((badge, index) => (
              <TrustBadge key={badge.title} badge={badge} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="testimonials-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">Vélemények</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#E5E5E5] mb-4">ÜGYFELEINK MONDTÁK</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={testimonial.name} testimonial={testimonial} index={index} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/velemenyek">
              <Button data-testid="all-reviews-btn" variant="outline" className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-4 uppercase tracking-widest rounded-sm">
                Összes Vélemény
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#121212] relative overflow-hidden" data-testid="cta-section">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#E5E5E5] mb-6">
              KÉSZEN ÁLL A<br /><span className="text-[#D4AF37]">PRÉMIUM ÉLMÉNYRE?</span>
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-8">Foglaljon időpontot még ma, és adja vissza autójának eredeti ragyogását.</p>
            <Link to="/idopontfoglalas">
              <Button data-testid="cta-booking-btn" className="btn-gold bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-10 py-6 rounded-sm text-lg">
                Időpont Foglalás <ArrowRight className="ml-2" size={22} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="map-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">Helyszín</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#E5E5E5] mb-4">TALÁLJON MEG MINKET</h2>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="border border-[#2A2A2A] rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d86239.48369406584!2d19.00497275!3d47.49433545!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741c334d1d4cfc9%3A0x400c4290c1e1160!2sBudapest!5e0!3m2!1shu!2shu!4v1699999999999!5m2!1shu!2shu"
                width="100%" height="400" style={{ border: 0, filter: "grayscale(100%) contrast(1.1)" }} allowFullScreen="" loading="lazy" title="Helyszín" data-testid="google-map"
              />
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-4 bg-[#0A0A0A] border border-[#2A2A2A]">
                <p className="text-[#D4AF37] font-heading mb-1">CÍM</p>
                <p className="text-[#A3A3A3] text-sm">1234 Budapest, Példa utca 123.</p>
              </div>
              <div className="p-4 bg-[#0A0A0A] border border-[#2A2A2A]">
                <p className="text-[#D4AF37] font-heading mb-1">TELEFON</p>
                <a href="tel:+36301234567" className="text-[#A3A3A3] text-sm hover:text-[#D4AF37]">+36 30 123 4567</a>
              </div>
              <div className="p-4 bg-[#0A0A0A] border border-[#2A2A2A]">
                <p className="text-[#D4AF37] font-heading mb-1">NYITVATARTÁS</p>
                <p className="text-[#A3A3A3] text-sm">H-P: 8:00-18:00 | Sz: 9:00-14:00</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
